import { useClientInvitationMutations, useClientInvitations } from '@/api/hooks';
import type { ClientOrg } from '@/api/schemasMembers';
import { Tag } from '@/components/badges';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InvitationsBody, type InvitationOps } from '@/features/members/PendingInvitations';
import { stagger } from '@/lib/motion';
import { ClientInviteForm } from './ClientInviteForm';

/**
 * People of a client (SPEC §17.6): invite an admin or a member, and resend or cancel the open invitations. The
 * operator acts inside the client's tenant and never becomes a member; the client's own admins manage the rest.
 */
export function ClientPeopleCard({ org, id, index }: { org: ClientOrg; id: string; index: number }) {
  const pending = useClientInvitations(org.id);
  const { resend, revoke } = useClientInvitationMutations(org.id);
  const ops: InvitationOps = { resend: resend.mutateAsync, revoke: revoke.mutateAsync, revoking: revoke.isPending };
  return (
    <Card id={id} data-testid="client-people" className="enter scroll-mt-24" style={stagger(index)}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          People
          <Tag>{org.memberCount} {org.memberCount === 1 ? 'member' : 'members'}</Tag>
        </CardTitle>
        <CardDescription>Invite the client's admins and members. Guests from other domains, roles and removals stay its admins' call.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <ClientInviteForm org={org} pending={pending.data} />
        <section aria-labelledby="client-invitations-title" data-testid="invitations-card" className="flex flex-col gap-3">
          <h3 id="client-invitations-title" className="flex items-center gap-2 text-sm font-semibold text-foreground">
            Pending invitations
            {pending.data ? <span data-testid="invitations-count"><Tag>{pending.data.length}</Tag></span> : null}
          </h3>
          <InvitationsBody pending={pending} ops={ops} emptyDescription="Nobody is waiting to join this client. Invitations you send appear here until they are accepted." />
        </section>
      </CardContent>
    </Card>
  );
}
